import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from '@radix-ui/react-icons';
import { Table } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../Select';
import { Button, ButtonProps } from '../../Button';
import { cn } from '../../../../../utils/styles';

export type IPaginationVariants = 'default' | 'extended' | 'guidance';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  variant?: IPaginationVariants;
}

const btnLastPageProps: ButtonProps = {
  variant: 'ghost',
  className: cn(['text-primary hidden h-8 w-8 p-0 !m-0 lg:flex', 'disabled:bg-transparent disabled:text-muted']),
  // hidden on small screens
};

export default function DataTablePagination<TData>({
  table,
  initialPageSize,
  variant,
}: DataTablePaginationProps<TData> & { initialPageSize?: number }) {
  const btnNextPageProps: ButtonProps = {
    variant: 'ghost', // 'extended' ? 'outline' : 'ghost'
    className: cn([
      'h-8 w-8 p-0',
      'disabled:bg-transparent disabled:text-muted !m-0',
      { 'text-primary': variant === 'default' },
    ]),
  };

  const tableState = table.getState();

  /** Calculate which rows is shown at the moment (example: 11-20 of 25) */
  const currentPagination = useMemo(
    () => ({
      itemsFrom:
        (tableState.pagination.pageIndex + 1) * tableState.pagination.pageSize - (tableState.pagination.pageSize - 1),
      itemsTo: Math.min(
        (tableState.pagination.pageIndex + 1) * tableState.pagination.pageSize,
        table.getFilteredRowModel()?.rows.length || 0,
      ),
    }),
    [tableState.pagination],
  );

  const pageSizeOptions = useMemo((): number[] => {
    const initialSizesList = [10, 20, 30, 40, 50];

    if (initialPageSize) {
      if (!initialSizesList.includes(initialPageSize)) {
        return [initialPageSize, ...initialSizesList];
      }
    }

    return initialSizesList;
  }, [initialPageSize]);

  return (
    <div
      className={cn('flex items-center justify-between px-[20px] py-2', {
        'py-0 mt-[25px]': variant === 'guidance',
      })}
    >
      <div
        className={cn('flex-1 flex items-center text-sm text-muted-foreground', {
          'justify-end': variant === 'guidance',
        })}
      >
        {/** Code which shows how many rows selected */}
        {/* <div className="row-selection-block">
          {table.getFilteredSelectedRowModel().rows.length}
          {' '}
          of
          {' '}
          {table.getFilteredRowModel().rows.length}
          {' '}
          row(s) selected.
        </div> */}
        <div className="total-items-count">
          {currentPagination.itemsFrom}-{currentPagination.itemsTo} of {table.getFilteredRowModel()?.rows.length || 0}
        </div>
        <div className="flex items-center space-x-2">
          {variant === 'extended' ? (
            <Button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              title="Go to first page"
              {...btnLastPageProps}
            >
              <span className="sr-only">Go to first page</span>
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            title="Go to prev page"
            {...btnNextPageProps}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex !m-0 items-center justify-center text-sm font-medium">
            {variant === 'guidance' ? (
              <div className="min-w-[40px] text-center text-sm text-addition1">
                {table.getState().pagination.pageIndex + 1}
              </div>
            ) : (
              <>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </>
            )}
          </div>
          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            title="Go to next page"
            {...btnNextPageProps}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          {variant === 'extended' ? (
            <Button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              title="Go to last page"
              {...btnLastPageProps}
            >
              <span className="sr-only">Go to last page</span>
              <DoubleArrowRightIcon className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {variant !== 'guidance' ? (
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">Results per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={value => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]" variant="transparent">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map(pageSize => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}
    </div>
  );
}

DataTablePagination.defaultProps = {
  initialPageSize: 10,
  variant: 'default',
};
